/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { FC, useMemo, useState } from 'react';
import { Button, Radio, Tag, Tooltip, message } from 'antd';
import {
  DownloadOutlined,
  DownOutlined,
  LikeOutlined,
  LikeFilled,
  DislikeFilled,
  MailOutlined,
  RightOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { OffersScreeningExpanded } from './OffersScreeningExpanded';
import {
  buildMergedScreeningVouchers,
  getDeliveryScoreColor,
  getScoresFromOfferData,
  getOfferVendorEmail,
  getOfferVendorName,
  ScreeningEvaluationValue,
  ScreeningOfferData,
  ScreeningOfferLike,
  ScreeningVoucherLike,
  ScreeningVoucherResponseLike,
} from './screeningDemoData';
import { downloadOfferZip } from '~src/utils/downloader/downloadOfferZip';
import { OfferReviewDrawer } from '~components/popups-modals/Review/OfferReviewDrawer';

type OffersScreeningRowProps = {
  projectId: number;
  tenderId: number;
  contractId: number;
  contractName?: string;
  offer: ScreeningOfferLike;
  serial: string;
  vouchers: ScreeningVoucherLike[];
  responses?: ScreeningVoucherResponseLike[];
  offerData?: ScreeningOfferData;
  screeningValue: ScreeningEvaluationValue;
  onScreeningChange: (offerId: number, value: ScreeningEvaluationValue) => void;
  isScreeningLocked?: boolean;
};

const OffersScreeningRowBase: FC<OffersScreeningRowProps> = ({
  projectId,
  tenderId,
  contractId,
  contractName,
  offer,
  serial,
  vouchers,
  responses = [],
  offerData,
  screeningValue,
  onScreeningChange,
  isScreeningLocked,
}) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isZipDownloading, setIsZipDownloading] = useState(false);
  const [reviewDrawerVisible, setReviewDrawerVisible] = useState(false);

  const mergedVouchers = useMemo(
    () => buildMergedScreeningVouchers(vouchers, responses, intl),
    [vouchers, responses, intl]
  );

  const scores = getScoresFromOfferData(offerData);

  const offerState = getOfferStateConfig(offer.state);
  const selectedScreeningValue =
    screeningValue === 3 || screeningValue === 4 ? screeningValue : undefined;

  const vendorName = getOfferVendorName(offer, intl);
  const vendorEmail = getOfferVendorEmail(offer);

  const reviewDashboard = offer.screening?.screening_binary_review_dashboard;
  const hasReviews = reviewDashboard && reviewDashboard.total_reviews > 0;
  const likesCount = reviewDashboard?.binary_positive_count ?? 0;
  const dislikesCount = reviewDashboard?.binary_negative_count ?? 0;

  const handleZipDownload = async () => {
    setIsZipDownloading(true);

    // Activate the download progress modal
    const { useStore } = await import('~src/store');
    const { DownloadingState } = await import('~src/store');
    useStore.getState().setIsOfferDownload(true);
    useStore.getState().changeDownloadingActive(true);
    useStore.getState().changeDownloadingState(DownloadingState.PREPARING);

    try {
      await downloadOfferZip({
        projectId,
        tenderId,
        contractId,
        offerId: offer.id,
        contractName: contractName || `Contract_${contractId}`,
        offerName: `${serial} ${vendorName}`,
        mergedVouchers,
        offerSerial: serial,
      });
    } catch {
      message.error(intl.formatMessage({ id: 'attachment-download-failed' }));
    } finally {
      setIsZipDownloading(false);
      // Modal will auto-close after FINISHING state (handled by saveFilesToZip)
    }
  };

  const requiredScoreColor = getDeliveryScoreColor(scores.required);
  const optionalScoreColor = getDeliveryScoreColor(scores.optional);
  const isScreeningControlsDisabled = !!isScreeningLocked;

  return (
    <div css={styles.wrapper}>
      <div css={styles.row}>
        <button
          type="button"
          css={styles.expandButton}
          onClick={() => setIsExpanded((prev) => !prev)}>
          {isExpanded ? <DownOutlined /> : <RightOutlined />}
        </button>

        <div css={styles.vendorCell}>
          <div css={styles.vendorTitleRow}>
            <UserOutlined css={styles.vendorIcon} />
            <span css={styles.vendorName}>
              {vendorName}
              <span css={styles.offerSerial}>{serial}</span>
            </span>
          </div>

          <div css={styles.vendorMetaRow}>
            <span css={styles.metaItem}>
              <MailOutlined />
              <span>{vendorEmail}</span>
            </span>
          </div>
        </div>

        <div css={styles.stateCell}>
          <div css={styles.cellLabel}>
            {intl.formatMessage({ id: 'screening-row-state-label' })}
          </div>
          <Tag color={offerState.color} css={styles.tag}>
            {intl.formatMessage({ id: offerState.intlId })}
          </Tag>
        </div>

        <div css={styles.scoreCell}>
          <div css={styles.cellLabel}>
            {intl.formatMessage({ id: 'screening-row-delivery-score-label' })}
          </div>
          <Tooltip
            title={intl.formatMessage(
              { id: 'screening-row-required-score-tooltip' },
              {
                responded: scores.required.delivered,
                total: scores.required.total,
              }
            )}>
            <span
              css={[
                styles.scoreBadge,
                {
                  borderColor: requiredScoreColor,
                  color: requiredScoreColor,
                },
              ]}>
              {scores.required.delivered}/{scores.required.total}
            </span>
          </Tooltip>
        </div>

        <div css={styles.scoreCell}>
          <div css={styles.cellLabel}>
            {intl.formatMessage({ id: 'screening-row-optional-score-label' })}
          </div>
          <Tooltip
            title={intl.formatMessage(
              { id: 'screening-row-optional-score-tooltip' },
              {
                responded: scores.optional.delivered,
                total: scores.optional.total,
              }
            )}>
            <span
              css={[
                styles.scoreBadge,
                {
                  borderColor: optionalScoreColor,
                  color: optionalScoreColor,
                },
              ]}>
              {scores.optional.delivered}/{scores.optional.total}
            </span>
          </Tooltip>
        </div>

        <div css={styles.screeningCell}>
          <div css={styles.cellLabel}>
            {intl.formatMessage({ id: 'screening-row-screening-label' })}
          </div>

          <Tooltip
            title={
              isScreeningLocked
                ? intl.formatMessage({
                    id: 'screening-row-locked-tooltip',
                  })
                : undefined
            }>
            <span css={styles.radioTooltipWrapper}>
              <Radio.Group
                size="small"
                value={selectedScreeningValue}
                disabled={isScreeningControlsDisabled}
                onChange={(event) =>
                  onScreeningChange(
                    offer.id,
                    Number(event.target.value) as ScreeningEvaluationValue
                  )
                }
                css={styles.screeningRadioGroup}>
                <Radio.Button value={3} className="screeningAcceptedOption">
                  {intl.formatMessage({ id: 'screening-row-accepted-option' })}
                </Radio.Button>
                <Radio.Button value={4} className="screeningRejectedOption">
                  {intl.formatMessage({ id: 'screening-row-rejected-option' })}
                </Radio.Button>
              </Radio.Group>
            </span>
          </Tooltip>
        </div>

        {/* REVIEW BLOCK */}
        <div css={styles.reviewCell}>
          <div css={styles.cellLabel}>
            {/* {intl.formatMessage({ id: 'screening-row-review-label' })} */}
          </div>

          <div
            css={styles.reviewDisplay}
            onClick={() => setReviewDrawerVisible(true)}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                setReviewDrawerVisible(true);
              }
            }}>
            {hasReviews ? (
              <div css={styles.reviewStats}>
                <span css={styles.likeCount}>
                  {likesCount > 0 ? <LikeFilled /> : <LikeOutlined />}
                  <span>{likesCount}</span>
                </span>
                <span css={styles.reviewDivider}>/</span>
                <span css={styles.dislikeCount}>
                  {dislikesCount > 0 ? (
                    <DislikeFilled />
                  ) : (
                    <LikeOutlined style={{ transform: 'scaleY(-1)' }} />
                  )}
                  <span>{dislikesCount}</span>
                </span>
              </div>
            ) : (
              <div css={styles.reviewEmpty}>
                <LikeOutlined />
                <span>
                  {intl.formatMessage({ id: 'review-no-reviews-row' })}
                </span>
                <span css={styles.reviewEmptyCount}>(0)</span>
              </div>
            )}
          </div>
        </div>

        <div css={styles.actionCell}>
          <Button
            size="small"
            css={styles.detailsButton}
            onClick={() => setIsExpanded((prev) => !prev)}>
            {isExpanded
              ? intl.formatMessage({ id: 'screening-row-hide-details' })
              : intl.formatMessage({ id: 'screening-row-view-details' })}
          </Button>

          <Tooltip
            title={intl.formatMessage({ id: 'evaluation-row-download-zip' })}>
            <Button
              size="small"
              icon={<DownloadOutlined />}
              loading={isZipDownloading}
              onClick={handleZipDownload}
              css={styles.zipButton}
              aria-label={intl.formatMessage({
                id: 'evaluation-row-download-all',
              })}
            />
          </Tooltip>
        </div>
      </div>

      {isExpanded && (
        <div css={styles.expandedContent}>
          <OffersScreeningExpanded
            projectId={projectId}
            tenderId={tenderId}
            contractId={contractId}
            offerId={offer.id}
            vouchers={mergedVouchers}
          />
        </div>
      )}

      <OfferReviewDrawer
        visible={reviewDrawerVisible}
        onClose={() => setReviewDrawerVisible(false)}
        projectId={projectId}
        tenderId={tenderId}
        contractId={contractId}
        offerId={offer.id}
        phase="screening"
        offerName={`${serial} ${vendorName}`}
        isReadOnly={isScreeningLocked}
      />
    </div>
  );
};

// Memoized so a single offer's review/dashboard update doesn't re-render every
// other row in the list. Requires stable props from the parent view.
export const OffersScreeningRow = React.memo(OffersScreeningRowBase);

const getOfferStateConfig = (state?: number) => {
  switch (state) {
    case 4:
      return {
        intlId: 'screening-row-offer-state-submitted',
        color: 'green',
      };
    case 3:
      return {
        intlId: 'screening-row-offer-state-reopened',
        color: 'orange',
      };
    case 1:
      return {
        intlId: 'screening-row-offer-state-in-progress',
        color: 'orange',
      };
    case 9:
      return {
        intlId: 'screening-row-offer-state-deleted',
        color: 'red',
      };
    default:
      return {
        intlId: 'screening-row-offer-state-unknown',
        color: 'default',
      };
  }
};

const styleFn: ThemedStyleFn = () => ({
  wrapper: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    border: '1px solid #e8e8e8',
    borderRadius: 6,
    background: '#ffffff',
    overflow: 'hidden',
    boxSizing: 'border-box',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
  },

  row: {
    display: 'grid',
    gridTemplateColumns:
      '24px minmax(240px, 1.2fr) minmax(90px, 0.4fr) minmax(110px, 0.46fr) minmax(110px, 0.46fr) minmax(150px, 0.6fr) 40px 200px',
    alignItems: 'center',
    gap: 16,
    minHeight: 86,
    padding: '14px 16px',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',

    '&:hover': {
      background: '#fafafa',
    },
  },

  expandButton: {
    width: 24,
    height: 24,
    padding: 0,
    border: 'none',
    background: 'transparent',
    color: '#595959',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,

    '&:hover': {
      color: '#1890ff',
    },
  },

  vendorCell: {
    minWidth: 0,
    overflow: 'hidden',
  },

  vendorTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
    overflow: 'hidden',
  },

  vendorIcon: {
    color: '#1890ff',
    flexShrink: 0,
  },

  vendorName: {
    fontWeight: 700,
    color: '#222222',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  offerSerial: {
    color: '#8c8c8c',
    fontWeight: 400,
    marginRight: 8,
    fontSize: '10px',
  },

  vendorMetaRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '6px 14px',
    marginTop: 7,
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    color: '#666666',
    fontSize: 12,
  },

  metaItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    minWidth: 0,
    overflow: 'hidden',

    span: {
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },

  stateCell: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    height: 49,
    minWidth: 0,
    width: '100%',
    overflow: 'hidden',
  },

  scoreCell: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    height: 49,
    minWidth: 0,
    width: '100%',
    overflow: 'hidden',
  },

  screeningCell: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    height: 49,
    minWidth: 0,
    width: '100%',
    overflow: 'hidden',
    flexShrink: 0,
  },

  radioTooltipWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    maxWidth: '100%',
    height: 32,
  },

  screeningRadioGroup: {
    display: 'inline-flex',
    alignItems: 'center',
    maxWidth: '100%',
    height: 32,

    '.ant-radio-button-wrapper': {
      height: 32,
      minHeight: 32,
      lineHeight: '30px',
      padding: '0 10px',
      fontSize: 11,
      fontWeight: 700,
      boxSizing: 'border-box',
      boxShadow: 'none',
    },

    '.ant-radio-button-wrapper:first-child': {
      borderTopLeftRadius: 4,
      borderBottomLeftRadius: 4,
    },

    '.ant-radio-button-wrapper:last-child': {
      borderTopRightRadius: 4,
      borderBottomRightRadius: 4,
    },

    '&& .screeningAcceptedOption:hover': {
      height: 32,
      minHeight: 32,
      color: '#52c41a',
      boxShadow: 'none',
    },

    '&& .screeningRejectedOption:hover': {
      height: 32,
      minHeight: 32,
      color: '#ff4d4f',
      boxShadow: 'none',
    },

    '&& .screeningAcceptedOption.ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled)': {
      height: 32,
      minHeight: 32,
      color: '#52c41a !important',
      borderColor: '#52c41a !important',
      borderRight: '1px solid #52c41a !important',
      background: '#f6ffed !important',
      boxShadow: 'none !important',
      zIndex: 3,
    },

    '&& .screeningAcceptedOption.ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled):hover': {
      height: 32,
      minHeight: 32,
      color: '#52c41a !important',
      borderColor: '#52c41a !important',
      borderRight: '1px solid #52c41a !important',
      background: '#f6ffed !important',
      boxShadow: 'none !important',
    },

    '&& .screeningAcceptedOption.ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled)::before': {
      display: 'none !important',
    },

    '&& .screeningAcceptedOption.ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) + .screeningRejectedOption': {
      borderLeft: '0 !important',
      marginLeft: '0 !important',
    },

    '&& .screeningAcceptedOption.ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) + .screeningRejectedOption::before': {
      display: 'none !important',
    },

    '&& .screeningAcceptedOption.ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled):hover + .screeningRejectedOption::before': {
      display: 'none !important',
    },

    '&& .screeningRejectedOption.ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled)': {
      height: 32,
      minHeight: 32,
      color: '#ff4d4f !important',
      borderColor: '#ff4d4f !important',
      borderLeft: '1px solid #ff4d4f !important',
      background: '#fff1f0 !important',
      boxShadow: 'none !important',
      zIndex: 3,
    },

    '&& .screeningRejectedOption.ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled):hover': {
      height: 32,
      minHeight: 32,
      color: '#ff4d4f !important',
      borderColor: '#ff4d4f !important',
      borderLeft: '1px solid #ff4d4f !important',
      background: '#fff1f0 !important',
      boxShadow: 'none !important',
    },

    '&& .screeningRejectedOption.ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled)::before': {
      display: 'none !important',
    },

    '.ant-radio-button-wrapper-disabled': {
      height: 32,
      minHeight: 32,
      lineHeight: '30px',
      boxShadow: 'none',
    },
  },

  reviewCell: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    height: 49,
    minWidth: 200,
    width: 200,
    overflow: 'visible',
    flexShrink: 0,
    marginLeft: -20,
  },

  reviewDisplay: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 32,
    cursor: 'pointer',
    borderRadius: 4,
    border: '1px solid #d3adf7',
    transition: 'all 0.2s',
    padding: '0 8px',

    '&:hover': {
      backgroundColor: '#f9f0ff',
      borderColor: '#b37feb',
    },
  },

  reviewStats: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    fontWeight: 500,
  },

  likeCount: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    color: '#52c41a',

    '.anticon': {
      fontSize: 16,
    },
  },

  dislikeCount: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    color: '#ff4d4f',

    '.anticon': {
      fontSize: 16,
    },
  },

  reviewDivider: {
    color: '#d9d9d9',
    fontWeight: 400,
  },

  reviewEmpty: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    color: '#8c8c8c',
    fontSize: 12,

    '.anticon': {
      fontSize: 14,
    },
  },

  reviewEmptyCount: {
    marginLeft: -2,
  },

  actionCell: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    gap: 8,
    height: 49,
    minWidth: 200,
    width: 200,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    boxSizing: 'border-box',
  },

  zipButton: {
    width: 32,
    minWidth: 32,
    height: 32,
    minHeight: 32,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    lineHeight: '30px',
    boxSizing: 'border-box',
    boxShadow: 'none',

    '&:hover, &:focus, &:active': {
      width: 32,
      minWidth: 32,
      height: 32,
      minHeight: 32,
      boxShadow: 'none',
    },
  },

  detailsButton: {
    width: 132,
    minWidth: 132,
    height: 32,
    minHeight: 32,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 14px',
    lineHeight: '30px',
    boxSizing: 'border-box',
    boxShadow: 'none',

    '&:hover, &:focus, &:active': {
      width: 132,
      minWidth: 132,
      height: 32,
      minHeight: 32,
      boxShadow: 'none',
    },

    span: {
      lineHeight: '30px',
    },
  },

  cellLabel: {
    height: 11,
    color: '#999999',
    fontSize: 11,
    fontWeight: 700,
    lineHeight: '11px',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    whiteSpace: 'nowrap',
  },

  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 'fit-content',
    maxWidth: '100%',
    height: 32,
    minHeight: 32,
    margin: 0,
    padding: '0 12px',
    lineHeight: '30px',
    fontWeight: 700,
    boxSizing: 'border-box',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  scoreBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 'fit-content',
    minWidth: 50,
    maxWidth: '100%',
    height: 32,
    minHeight: 32,
    padding: '0 10px',
    border: '1px solid',
    borderRadius: 16,
    background: '#ffffff',
    fontWeight: 700,
    fontSize: 12,
    lineHeight: '30px',
    boxSizing: 'border-box',
    whiteSpace: 'nowrap',
  },

  reviewPlaceholder: {
    display: 'block',
    color: '#999999',
    fontSize: 12,
    fontStyle: 'italic',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  reviewButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 128,
    maxWidth: '100%',
    height: 32,
    minHeight: 32,
    padding: '0 14px',
    lineHeight: '30px',
    color: '#722ed1',
    borderColor: '#d3adf7',
    background: '#ffffff',
    boxSizing: 'border-box',
    boxShadow: 'none',

    '&:hover, &:focus, &:active': {
      height: 32,
      minHeight: 32,
      color: '#531dab',
      borderColor: '#9254de',
      background: '#ffffff',
      boxShadow: 'none',
    },

    '.anticon': {
      display: 'inline-flex',
      alignItems: 'center',
      color: '#faad14',
    },
  },

  reviewIconButton: {
    width: 32,
    minWidth: 32,
    height: 32,
    minHeight: 32,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    borderColor: '#d3adf7',
    background: '#ffffff',
    boxSizing: 'border-box',
    boxShadow: 'none',
    cursor: 'pointer',

    '.anticon': {
      display: 'inline-flex',
      alignItems: 'center',
      fontSize: 16,
      color: '#faad14',
    },

    '&:hover, &:focus, &:active': {
      width: 32,
      minWidth: 32,
      height: 32,
      minHeight: 32,
      borderColor: '#9254de',
      background: '#ffffff',
      boxShadow: 'none',

      '.anticon': {
        color: '#fa8c16',
      },
    },
  },

  expandedContent: {
    padding: '0 16px 16px 16px',
    background: '#fcfcfc',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
  },
});
